import React, { useEffect, useMemo, useRef } from "react";
import styled from "styled-components";

import {
  useGetRoleOfferPrefillQuery,
  type CompensationLine,
  type RoleOfferPrefill,
} from "../../../app/services/roles";
import {
  useGetOfferHistoryQuery,
  type OfferHistoryItem,
} from "../../../app/services/dmThreads";
// @ts-ignore - JS コンポーネント（型定義なし）
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import { formatYen, toYenString } from "../../../utils/money";
import { addConsumptionTax } from "../../../utils/tax";
import {
  evaluateOfferEscrowBalanceCheck,
  type OfferEscrowBalanceCheck,
} from "../../../utils/balance";
import { computeCompensationSubtotalExclusive } from "../../../utils/offerCompensationLines";
import Input from "../../form-item/Input";
import ComboInput from "../../form-item/ComboInput";
import Textarea from "../../form-item/Textarea";
import Toggle from "../../form-item/Toggle";
// @ts-ignore - JSX コンポーネント（型定義なし）
import DateInput from "../../calendar/DateInput";
import Button from "../../button/Button";
import CompensationLinesEditor, {
  FALLBACK_COMPENSATION_LINE,
} from "../CompensationLinesEditor";
import {
  Accordion,
  AccordionBody,
  AccordionSummary,
  BalanceNoticeWarning,
  FieldLabel,
  FieldRow,
  FormRoot as StepRoot,
  FormSection as Section,
  FormSectionTitle as SectionTitle,
  InlineRow,
} from "../OfferFormLayout";
import {
  fixedSubcontractingLabel,
  normalizeAcceptPosition,
  resolveAllowSubcontracting,
} from "../roleAcceptPosition";
import PrefillSourcePicker from "./PrefillSourcePicker";
import type {
  OfferFormState,
  OfferCompensationLine,
  OfferPrefillSource,
  OfferRecipientState,
} from "../types";

interface DetailFormStepProps {
  roleId: number;
  form: OfferFormState;
  onChange: (next: OfferFormState) => void;
  /** 対価と残高の比較結果を親（OfferWizardOverlay）へ通知する */
  onEscrowBalanceChange?: (check: OfferEscrowBalanceCheck) => void;
  /** 他の未送信ドラフトが予約している対価合計（税抜）。利用可能額から差し引く。 */
  reservedByOtherDraftsExclusive?: number;
  /**
   * 送信先の状態。過去オファー履歴（同じ相手 × 同じロール名）の検索に使う。
   * 未指定のとき履歴カードは表示されない。
   */
  recipient?: OfferRecipientState;
  /** 履歴一覧から除外する DM ID（編集中の自スレッドが混入するのを防ぐ） */
  excludeDmId?: string;
  /** 現在のプリフィル元（未指定はテンプレート扱い） */
  prefillSource?: OfferPrefillSource;
  /** プリフィル元カードの選択時に呼ばれる（選択状態の永続化用） */
  onPrefillSourceChange?: (source: OfferPrefillSource) => void;
}

// 消費税（仮定値）。BE と仕様統一する場合は constant ファイルに切り出す。
// TODO(BE): 税率定義は BE と同期する。
const TAX_RATE = 0.1;

// プリフィルが空のときの FE デフォルト
const FALLBACK_DELIVERABLE_FORMAT = "クライアントが指定する形式";
const FALLBACK_DELIVERY_METHOD = "SOREMO OS上";
const FALLBACK_DELIVERY_LOCATION = "SOREMO OS上";

// ===== レイアウト =====

const ErrorBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--soremo-green);
  border-radius: 8px;
  background: rgba(255, 59, 48, 0.04);
`;

const ErrorTitle = styled.div.attrs({ className: "heading-13" })`
  color: var(--soremo-green);
`;

const ErrorBody = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-text);
`;

const PlaceholderText = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
  padding: 24px 0;
  text-align: center;
`;

// producer / director ロール時に「再委託を認める」トグルの代わりに表示する固定ラベル
const FixedSubcontractingLabel = styled.div.attrs({ className: "bodytext-13 u-pl4" })`
  color: var(--soremo-text);
  padding: 8px 0;
`;

// ===== ヘルパー =====

const linesFromApi = (lines: CompensationLine[] | undefined): OfferCompensationLine[] => {
  const mapped = (lines ?? []).map((l) => ({
    label: l.label,
    // 円は整数のみ。API/DB 由来の小数文字列をここで整数へ正規化する。
    unitPrice: toYenString(l.unit_price),
    quantity: l.quantity,
    unit: l.unit ?? "",
  }));
  return mapped.length > 0 ? mapped : [FALLBACK_COMPENSATION_LINE];
};

const buildIsoFromOffset = (
  offsetDays: number,
  time: string | undefined,
  baseDate: Date = new Date(),
): string => {
  const target = new Date(baseDate);
  target.setHours(0, 0, 0, 0);
  target.setDate(target.getDate() + offsetDays);
  if (time) {
    const [hh, mm] = time.split(":").map((n) => Number.parseInt(n, 10) || 0);
    target.setHours(hh, mm, 0, 0);
  }
  return target.toISOString();
};

const buildContractIso = (
  offsetDays: number | null | undefined,
  baseDate: Date = new Date(),
): string | null => {
  if (offsetDays === null || offsetDays === undefined) return null;
  return buildIsoFromOffset(offsetDays, "00:00", baseDate);
};

/** 翌月初日 (YYYY-MM-01) の ISO 文字列 */
const nextMonthStartIso = (baseDate: Date = new Date()): string => {
  const target = new Date(baseDate);
  target.setHours(0, 0, 0, 0);
  target.setDate(1);
  target.setMonth(target.getMonth() + 1);
  return target.toISOString();
};

/** 翌月末日 (YYYY-MM-末日) の ISO 文字列 */
const nextMonthEndIso = (baseDate: Date = new Date()): string => {
  const target = new Date(baseDate);
  target.setHours(0, 0, 0, 0);
  // 翌月末日 = 翌々月の 0 日
  target.setDate(1);
  target.setMonth(target.getMonth() + 2);
  target.setDate(0);
  return target.toISOString();
};

const isoToDate = (iso: string | undefined | null): Date | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

/**
 * テンプレート（offer-prefill）由来のフォーム値一式を構築する。
 *
 * 初回プリフィルの useEffect（未入力のみ埋める）とは別に、プリフィル元カードで
 * 「テンプレート」を明示選択したときの **上書き** 適用に使う。
 * 日付系は常に「今日 + offset」の相対計算で作り直す。
 */
const buildTemplateFormValues = (
  prefill: RoleOfferPrefill,
  baseDate: Date,
): Omit<OfferFormState, "isTransactional" | "masterOfferForm"> => {
  const fromDb = prefill.deliverable_format_options?.[0];
  return {
    roleName: prefill.role.role_name_jp,
    allowSubcontracting: resolveAllowSubcontracting(
      prefill.defaults.accept_position,
      prefill.defaults.allow_subcontracting,
    ),
    acceptPosition: normalizeAcceptPosition(prefill.defaults.accept_position),
    deliverableFormat:
      fromDb !== undefined && fromDb !== null
        ? fromDb
        : FALLBACK_DELIVERABLE_FORMAT,
    deadline: buildIsoFromOffset(
      prefill.defaults.deadline_offset_days,
      prefill.defaults.deadline_time,
      baseDate,
    ),
    compensationLines: linesFromApi(prefill.compensation_lines),
    specialProvisionsText: (prefill.special_provisions_options ?? []).join("\n"),
    scope: prefill.defaults.scope,
    message: prefill.defaults.initial_message,
    details: {
      validUntil: buildIsoFromOffset(
        prefill.defaults.valid_until_offset_days,
        prefill.defaults.valid_until_time,
        baseDate,
      ),
      contractStartDate:
        buildContractIso(prefill.defaults.contract_start_offset_days, baseDate) ??
        nextMonthStartIso(baseDate),
      contractEndDate:
        buildContractIso(prefill.defaults.contract_end_offset_days, baseDate) ??
        nextMonthEndIso(baseDate),
      deliveryMethod: prefill.defaults.delivery_method || FALLBACK_DELIVERY_METHOD,
      deliveryLocation:
        prefill.defaults.delivery_location || FALLBACK_DELIVERY_LOCATION,
    },
  };
};

/**
 * 過去オファーの内容系フィールドをテンプレートベースのフォームへ上書きする。
 *
 * 日付系（納期・有効期限・契約期間）は過去日付の混入を避けるため `base`
 * （= テンプレートの相対計算値）を維持し、金額・メッセージ等の内容だけを反映する。
 * `allowSubcontracting` は PRODUCER / DIRECTOR ロールの強制 true を維持するため
 * `resolveAllowSubcontracting` を通す。
 */
const applyHistoryValues = (
  base: OfferFormState,
  item: OfferHistoryItem,
  prefill: RoleOfferPrefill,
): OfferFormState => ({
  ...base,
  roleName: item.assigned_roles ?? base.roleName,
  allowSubcontracting: resolveAllowSubcontracting(
    prefill.defaults.accept_position,
    item.allow_subcontracting,
  ),
  acceptPosition:
    normalizeAcceptPosition(item.accept_position) ?? base.acceptPosition,
  advancePaymentRate: item.advance_payment_rate,
  requiresDeliverable: item.requires_deliverable,
  deliverableFormat: item.deliverable_format ?? base.deliverableFormat,
  compensationLines:
    item.compensation_lines.length > 0
      ? linesFromApi(item.compensation_lines)
      : base.compensationLines,
  quantity: item.quantity ?? base.quantity,
  specialProvisionsText: item.special_provisions ?? "",
  scope: item.scope ?? base.scope,
  message: item.initial_message ?? base.message,
  details: {
    ...base.details,
    deliveryMethod: item.delivery_method ?? base.details?.deliveryMethod,
    deliveryLocation: item.delivery_location ?? base.details?.deliveryLocation,
  },
});

// ===== 本体 =====

const DetailFormStep: React.FC<DetailFormStepProps> = ({
  roleId,
  form,
  onChange,
  onEscrowBalanceChange,
  reservedByOtherDraftsExclusive = 0,
  recipient,
  excludeDmId,
  prefillSource,
  onPrefillSourceChange,
}) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const {
    data: prefillData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetRoleOfferPrefillQuery(roleId);

  // ─── 過去オファー履歴（同じ相手 × 同じロール名）───
  // ロール名はテンプレート（ロールマスタ）の role_name_jp で照合する。
  // 送信先が未確定（recipient 無し / userId・email どちらも無し）の場合はスキップ。
  const historyRoleName = prefillData?.data?.role?.role_name_jp ?? "";
  const historyRecipientUserId =
    recipient?.mode === "follows" ? recipient.userId : undefined;
  const historyRecipientEmail =
    recipient?.mode === "email" ? recipient.email : undefined;
  const skipHistory =
    !historyRoleName ||
    (historyRecipientUserId === undefined && !historyRecipientEmail);
  const { data: historyData } = useGetOfferHistoryQuery(
    {
      role_name: historyRoleName,
      contractor_user_id: historyRecipientUserId,
      recipient_email: historyRecipientEmail,
      limit: 10,
    },
    { skip: skipHistory },
  );
  const historyItems = useMemo<OfferHistoryItem[]>(() => {
    const items = historyData?.data?.offers ?? [];
    // 編集中の自スレッド（送信済みオファーの編集モード）は除外する
    return excludeDmId
      ? items.filter((item) => item.dm_id !== excludeDmId)
      : items;
  }, [historyData, excludeDmId]);

  const selectedPrefillSource: OfferPrefillSource =
    prefillSource ?? { kind: "template" };

  /**
   * プリフィル元カードの選択。テンプレート値でフォームを再構築し、
   * 履歴選択時はその上に内容系フィールドを上書きする（日付はテンプレート維持）。
   */
  const handleSelectPrefillSource = (source: OfferPrefillSource) => {
    onPrefillSourceChange?.(source);
    const prefill = prefillData?.data;
    if (!prefill) return;
    const templateForm: OfferFormState = {
      ...form,
      ...buildTemplateFormValues(prefill, new Date()),
    };
    if (source.kind === "template") {
      onChange(templateForm);
      return;
    }
    const item = historyItems.find((h) => h.dm_id === source.dmId);
    if (!item) return;
    onChange(applyHistoryValues(templateForm, item, prefill));
  };

  // 直前にプリフィル初期化した roleId を記録。ロール変更を検知して
  // フォーム全体を新しいロール用にリセットする。
  const lastInitRoleIdRef = useRef<number | null>(null);

  // プリフィル取得後、未入力フィールドだけを「今日 + offset」で初期化する。
  // ロール変更時は前ロールの値を引きずらないように空ベースから再構築する。
  useEffect(() => {
    const prefill = prefillData?.data;
    if (!prefill) return;

    // ロール ID が前回初期化時と変わっていれば、フォームを再初期化する。
    const isRoleChanged =
      lastInitRoleIdRef.current !== null && lastInitRoleIdRef.current !== roleId;

    // 再初期化時はベースを空にして各フィールドをすべて新ロールの値で埋める。
    const next: OfferFormState = isRoleChanged
      ? { allowSubcontracting: false }
      : { ...form };
    let dirty = isRoleChanged;
    const baseDate = new Date();

    // 初回プリフィル時の判定: roleName が未設定 = この form は初期化前
    const isFirstFill = next.roleName === undefined;

    if (isFirstFill) {
      next.roleName = prefill.role.role_name_jp;
      // producer / director は再委託可を内包するため強制的に true。
      next.allowSubcontracting = resolveAllowSubcontracting(
        prefill.defaults.accept_position,
        prefill.defaults.allow_subcontracting,
      );
      dirty = true;
    }

    // ロール由来の accept_position をフォームに反映する。再初期化（初回 /
    // ロール変更）時は next がリセットされ undefined になるためここで埋まる。
    // 既に値が入っている場合は尊重するが、null（変更なし）かつ prefill に値が
    // ある未保存ドラフトは救済的に prefill 値で上書きする。
    const prefillAcceptPosition = normalizeAcceptPosition(
      prefill.defaults.accept_position,
    );
    if (
      next.acceptPosition === undefined ||
      (next.acceptPosition === null && prefillAcceptPosition !== null)
    ) {
      next.acceptPosition = prefillAcceptPosition;
      dirty = true;
    }

    if (next.deliverableFormat === undefined) {
      // DB 由来の値があればそれを優先（空文字も尊重）。
      // フィールド/配列が完全に存在しない場合のみ FALLBACK を使う。
      const fromDb = prefill.deliverable_format_options?.[0];
      next.deliverableFormat =
        fromDb !== undefined && fromDb !== null
          ? fromDb
          : FALLBACK_DELIVERABLE_FORMAT;
      dirty = true;
    }

    if (next.deadline === undefined) {
      next.deadline = buildIsoFromOffset(
        prefill.defaults.deadline_offset_days,
        prefill.defaults.deadline_time,
        baseDate,
      );
      dirty = true;
    }

    if (next.compensationLines === undefined) {
      next.compensationLines = linesFromApi(prefill.compensation_lines);
      dirty = true;
    }

    if (next.specialProvisionsText === undefined) {
      next.specialProvisionsText = (prefill.special_provisions_options ?? []).join(
        "\n",
      );
      dirty = true;
    }

    if (next.scope === undefined) {
      next.scope = prefill.defaults.scope;
      dirty = true;
    }

    if (next.message === undefined) {
      next.message = prefill.defaults.initial_message;
      dirty = true;
    }

    if (next.details === undefined) {
      const validUntilIso = buildIsoFromOffset(
        prefill.defaults.valid_until_offset_days,
        prefill.defaults.valid_until_time,
        baseDate,
      );
      // 契約期間: prefill が null の場合は「翌月頭〜翌月末」をフォールバック
      const contractStartIso =
        buildContractIso(prefill.defaults.contract_start_offset_days, baseDate) ??
        nextMonthStartIso(baseDate);
      const contractEndIso =
        buildContractIso(prefill.defaults.contract_end_offset_days, baseDate) ??
        nextMonthEndIso(baseDate);
      next.details = {
        validUntil: validUntilIso,
        contractStartDate: contractStartIso,
        contractEndDate: contractEndIso,
        deliveryMethod: prefill.defaults.delivery_method || FALLBACK_DELIVERY_METHOD,
        deliveryLocation:
          prefill.defaults.delivery_location || FALLBACK_DELIVERY_LOCATION,
      };
      dirty = true;
    }

    if (dirty) {
      onChange(next);
    }
    lastInitRoleIdRef.current = roleId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData, roleId]);

  const deliverableOptions = useMemo<string[]>(
    () =>
      (prefillData?.data?.deliverable_format_options ?? []).filter(
        (v): v is string => typeof v === "string" && v.length > 0,
      ),
    [prefillData],
  );

  const subtotalExclTax = useMemo(
    () => computeCompensationSubtotalExclusive(form.compensationLines),
    [form.compensationLines],
  );
  const taxAmount = subtotalExclTax * TAX_RATE;
  const totalInclTax = subtotalExclTax + taxAmount;

  const escrowBalanceCheck = useMemo(
    () =>
      evaluateOfferEscrowBalanceCheck({
        contractAmountExclusive: subtotalExclTax,
        userData: currentUser?.data,
        reservedByOtherDraftsExclusive,
      }),
    [subtotalExclTax, currentUser, reservedByOtherDraftsExclusive],
  );

  useEffect(() => {
    onEscrowBalanceChange?.(escrowBalanceCheck);
  }, [escrowBalanceCheck, onEscrowBalanceChange]);

  const showBalanceWarning =
    escrowBalanceCheck.applicable && !escrowBalanceCheck.sufficient;
  const availableInclTax = addConsumptionTax(escrowBalanceCheck.availableExclusive);
  const shortfallInclTax = addConsumptionTax(escrowBalanceCheck.shortfallExclusive);
  const reservedInclTax = addConsumptionTax(
    escrowBalanceCheck.reservedByOtherDraftsExclusive,
  );
  const hasReserved = escrowBalanceCheck.reservedByOtherDraftsExclusive > 0;

  if (isError) {
    const status = (error as { status?: number | string } | undefined)?.status;
    return (
      <StepRoot>
        <ErrorBox>
          <ErrorTitle>雛形の取得に失敗しました</ErrorTitle>
          <ErrorBody>
            offer-prefill API でサーバエラーが発生しています
            （HTTP {status ?? "不明"}）。「戻る」でロール選択に戻り別の
            ロールを試すか、時間をおいて再試行してください。
          </ErrorBody>
          <Button
            size="small"
            variant="outlined"
            theme="secondary"
            onClick={() => refetch()}
          >
            再試行
          </Button>
        </ErrorBox>
      </StepRoot>
    );
  }

  if (isLoading || !prefillData) {
    return (
      <StepRoot>
        <PlaceholderText>雛形を読み込み中...</PlaceholderText>
      </StepRoot>
    );
  }

  const setDateField = (
    fieldPath: "deadline" | "validUntil" | "contractStartDate" | "contractEndDate",
    value: Date | null,
  ) => {
    const iso = value ? value.toISOString() : null;
    if (fieldPath === "deadline") {
      // 納期変更時、契約終了日が新しい納期より前なら自動的に納期に合わせる
      let nextDetails = form.details;
      if (iso && form.details?.contractEndDate) {
        const newDeadline = new Date(iso);
        const endDate = new Date(form.details.contractEndDate);
        if (
          !Number.isNaN(newDeadline.getTime()) &&
          !Number.isNaN(endDate.getTime()) &&
          endDate.getTime() < newDeadline.getTime()
        ) {
          nextDetails = { ...form.details, contractEndDate: iso };
        }
      }
      onChange({
        ...form,
        deadline: iso ?? undefined,
        details: nextDetails,
      });
      return;
    }
    onChange({
      ...form,
      details: {
        ...form.details,
        [fieldPath]: iso,
      },
    });
  };

  const compensationLines = form.compensationLines ?? [];

  return (
    <StepRoot>
      {/* プリフィル元セレクタ（過去オファー履歴があるときのみ表示） */}
      {historyItems.length > 0 && (
        <Section>
          <PrefillSourcePicker
            historyItems={historyItems}
            selected={selectedPrefillSource}
            onSelect={handleSelectPrefillSource}
          />
        </Section>
      )}

      {/* ロール（テキストとして自由編集） + 再委託トグル */}
      <Section>
        <SectionTitle>ロール</SectionTitle>
        <FieldRow>
          <Input
            label="ロール名"
            option="required"
            value={form.roleName ?? ""}
            onChange={(e) => onChange({ ...form, roleName: e.target.value })}
            placeholder="例: プロデュース"
          />
        </FieldRow>
        <FieldRow>
          {(() => {
            const acceptPosition = prefillData.data.defaults.accept_position;
            const fixedLabel = fixedSubcontractingLabel(acceptPosition);
            if (fixedLabel) {
              return (
                <FixedSubcontractingLabel>{fixedLabel}</FixedSubcontractingLabel>
              );
            }
            return (
              <Toggle
                value={form.allowSubcontracting}
                onClick={() =>
                  onChange({
                    ...form,
                    allowSubcontracting: !form.allowSubcontracting,
                  })
                }
              >
                再委託を認める
              </Toggle>
            );
          })()}
        </FieldRow>
      </Section>

      {/* 条件 */}
      <Section>
        <SectionTitle>条件</SectionTitle>

        <FieldRow>
          <ComboInput
            label="納品形式"
            value={form.deliverableFormat ?? ""}
            onChange={(e) =>
              onChange({ ...form, deliverableFormat: e.target.value })
            }
            options={deliverableOptions}
            placeholder="納品形式を入力または選択"
          />
        </FieldRow>

        <FieldRow>
          <DateInput
            label="納期"
            option="required"
            value={isoToDate(form.deadline)}
            onChange={(e: { target: { value: Date | null } }) =>
              setDateField("deadline", e.target.value)
            }
            options={{ enableTime: true }}
            placeholder="納期を選択"
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel text="対価" option="required" />

          <CompensationLinesEditor
            lines={compensationLines}
            onChange={(lines) =>
              onChange({ ...form, compensationLines: lines })
            }
          />

          {showBalanceWarning && (
            <BalanceNoticeWarning>
              利用可能残高（{formatYen(availableInclTax)}）が対価合計（
              {formatYen(totalInclTax)}）に足りません（不足{" "}
              {formatYen(shortfallInclTax)}）。残高を追加するか、対価を調整してください。
              {hasReserved && (
                <>
                  {" "}
                  ※他の未送信オファー（{formatYen(reservedInclTax)}
                  ）の予約分を差し引いています。
                </>
              )}
            </BalanceNoticeWarning>
          )}
        </FieldRow>

        <FieldRow>
          <FieldLabel text="特記事項" option="optional" />
          <Textarea
            rows={3}
            placeholder="特記事項を入力"
            value={form.specialProvisionsText ?? ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onChange({ ...form, specialProvisionsText: e.target.value })
            }
          />
        </FieldRow>
      </Section>

      {/* 内容 */}
      <Section>
        <SectionTitle>内容</SectionTitle>

        <FieldRow>
          <Input
            label="スコープ"
            option="optional"
            value={form.scope ?? ""}
            onChange={(e) => onChange({ ...form, scope: e.target.value })}
          />
        </FieldRow>

        <FieldRow>
          <FieldLabel text="メッセージ" option="optional" />
          <Textarea
            rows={7}
            value={form.message ?? ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onChange({ ...form, message: e.target.value })
            }
          />
        </FieldRow>
      </Section>

      {/* 詳細 */}
      <Accordion>
        <AccordionSummary>
          <span>詳細</span>
          <span className="material-symbols-rounded">expand_more</span>
        </AccordionSummary>
        <AccordionBody>
          <FieldRow>
            <DateInput
              label="有効期限"
              option="optional"
              value={isoToDate(form.details?.validUntil)}
              onChange={(e: { target: { value: Date | null } }) =>
                setDateField("validUntil", e.target.value)
              }
              options={{ enableTime: true }}
              placeholder="有効期限を選択"
            />
          </FieldRow>

          <FieldRow>
            <FieldLabel text="契約期間" option="optional" />
            <InlineRow>
              <DateInput
                value={isoToDate(form.details?.contractStartDate)}
                onChange={(e: { target: { value: Date | null } }) =>
                  setDateField("contractStartDate", e.target.value)
                }
                options={{ dateFormat: "Y/m/d" }}
                placeholder="開始日"
              />
              <DateInput
                value={isoToDate(form.details?.contractEndDate)}
                onChange={(e: { target: { value: Date | null } }) =>
                  setDateField("contractEndDate", e.target.value)
                }
                options={{ dateFormat: "Y/m/d" }}
                placeholder="終了日"
              />
            </InlineRow>
          </FieldRow>

          <FieldRow>
            <Input
              label="取引方法"
              option="optional"
              value={form.details?.deliveryMethod ?? ""}
              onChange={(e) =>
                onChange({
                  ...form,
                  details: { ...form.details, deliveryMethod: e.target.value },
                })
              }
            />
          </FieldRow>

          <FieldRow>
            <Input
              label="引渡場所"
              option="optional"
              value={form.details?.deliveryLocation ?? ""}
              onChange={(e) =>
                onChange({
                  ...form,
                  details: { ...form.details, deliveryLocation: e.target.value },
                })
              }
            />
          </FieldRow>
        </AccordionBody>
      </Accordion>
    </StepRoot>
  );
};

export default DetailFormStep;
